import { useAtomValue } from 'jotai';

import { Dialog, DialogContent, DialogTitle } from '@rocco/components/dialog';
import { cn } from '@rocco/utils/cn';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import type { TeamMemberView } from '@modules/professional/type/team-member';
import TeamMemberModalContent from '@modules/professional/component/about-tab/team-member/TeamMemberModalContent';
import { hasRenderableTeamMemberSummary } from '@modules/professional/component/about-tab/team-member/hasRenderableTeamMemberSummary';
import BottomDrawer from '~/modules/detail/components/bottom-drawer/BottomDrawer';

interface TeamMemberDialogProps {
    member: TeamMemberView;
    website: string;
    professionalId?: number;
    onClose: () => void;
}

/**
 * A team member's card in the site's own dialog: the rounded corners and the
 * dark close disc every other modal on the page carries, around the legacy
 * card's content. Wide when there is a biography to set beside the portrait,
 * narrow when the portrait stands alone. A phone keeps the bottom sheet.
 */
export const TeamMemberDialog = ({
    member,
    website,
    professionalId,
    onClose,
}: TeamMemberDialogProps) => {
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    const hasSummary = hasRenderableTeamMemberSummary(member.summary);

    if (isMobile) {
        return (
            <BottomDrawer open toggleModal={onClose}>
                <TeamMemberModalContent
                    website={website}
                    memberDetails={member}
                    professionalId={professionalId}
                />
            </BottomDrawer>
        );
    }

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent
                variant="default"
                spacing="none"
                closeButtonVariant="rounded"
                closeButtonClassName="top-[1.4375rem] right-[1.375rem] size-[2.625rem] bg-[#181818] text-white shadow-none hover:bg-[#2c2c2c]"
                className={cn(
                    'block max-h-[90vh] max-w-[95vw] overflow-x-hidden overflow-y-auto border-0 bg-white p-0',
                    'rounded-[1.5rem] sm:rounded-[1.5rem]',
                    hasSummary ? 'w-[77.5rem]' : 'w-[38.75rem]',
                )}
            >
                <DialogTitle className="sr-only">{member.name}</DialogTitle>
                {/* The card is two panes, portrait and copy, each running the
                    full height. Top padding belongs to the copy — on the
                    wrapper it painted the dialog's own white across the top of
                    both, leaving a band above the portrait panel. The selector
                    reaches the copy column because the pane markup is Aria's,
                    whose class names are generated and cannot be targeted. */}
                <div className="[&>div>div:last-child]:pt-[4.6875rem]">
                    <TeamMemberModalContent
                        website={website}
                        memberDetails={member}
                        professionalId={professionalId}
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
};
